// Raw Greenway design values from Figma (file z3ptvnEQi4u7P2NsTTUWfA, node 68:149).
// Never reference feature code from here. Consume via the design-tokens barrel or generated SCSS.

export const palette = {
  // Figma 'white' group — near-white tints, named per Figma file (not a true greyscale ramp).
  white: {
    50: '#FEFEFE',
    100: '#FCFCFC',
    200: '#FAFAFA',
    300: '#F8F8F8',
    400: '#F6F7F7',
    500: '#F4F5F5',
    600: '#DEDFDF',
    700: '#ADAEAE',
    800: '#868787',
    900: '#666767',
  },
  lightGreen: {
    50: '#FCFDFD',
    100: '#F7F8F7',
    200: '#F3F5F4',
    300: '#EEF1EE',
    400: '#EAEEEB',
    500: '#E5EAE6',
    600: '#D0D5D1',
    700: '#A3A6A3',
    800: '#7E817F',
    900: '#606261',
  },
  lightBrown: {
    50: '#FCFBFA',
    100: '#F5F4EF',
    200: '#F0EEE8',
    300: '#E9E7DD',
    400: '#E5E2D6',
    500: '#DEDBCC',
    600: '#CAC7BA',
    700: '#9E9B91',
    800: '#7A7870',
    900: '#5D5C56',
  },
  greenPrimary: {
    50: '#E6E9E8',
    100: '#B1BBB6',
    200: '#8B9B93',
    300: '#556D62',
    400: '#355144',
    500: '#022515',
    600: '#022213',
    700: '#011A0F',
    800: '#01140C',
    900: '#011009',
  },
  greenSecondary: {
    50: '#EBF2EF',
    100: '#C2D6CE',
    200: '#A5C2B6',
    300: '#7CA694',
    400: '#629580',
    500: '#3B7A60',
    600: '#366F57',
    700: '#2A5744',
    800: '#204335',
    900: '#193328',
  },
  accent1: {
    50: '#FEFFEE',
    100: '#FBFFCB',
    200: '#F9FFB2',
    300: '#F6FF8F',
    400: '#F4FF79',
    500: '#F1FF58',
    600: '#DBE850',
    700: '#ABB53E',
    800: '#858C30',
    900: '#656B25',
  },
  accent2: {
    50: '#FCFFF5',
    100: '#F7FDDF',
    200: '#F3FDD0',
    300: '#EEFCBB',
    400: '#EAFBAD',
    500: '#E5FA99',
    600: '#D0E48B',
    700: '#A3B26D',
    800: '#7E8A54',
    900: '#606940',
  },
  brand: {
    50: '#FAFDF5',
    100: '#F4FBEA',
    200: '#E8F7CF',
    300: '#D3FA99',
    400: '#B7EA8B',
    500: '#8ED45A',
    600: '#68B631',
    700: '#3D5B34',
    800: '#1C3A13',
    900: '#162E0F',
    950: '#142F0C',
  },
  neutral: {
    50: '#FCFCF7',
    100: '#F4F4F4',
    200: '#E9E9E9',
    300: '#CCCCCC',
    400: '#AAAAAA',
    500: '#999999',
    600: '#777777',
    700: '#555555',
    800: '#333333',
    900: '#1A1A1A',
    950: '#111111',
  },
  success: { 100: '#E8FDF5', 700: '#19A974', 900: '#137752' },
  warning: { 100: '#FFFCEB', 500: '#FFD700', 700: '#FFB700' },
  error: { 100: '#FFDFDF', 500: '#FF725C', 700: '#FF4136', 900: '#E7040F' },
  info: { 100: '#CDECFF', 300: '#96CCFF', 500: '#6AAAE4', 700: '#357EDD', 900: '#00449E' },
  // Chip stage colors (Figma node 121:1927) + control hover surface
  // (Figma --default/default-hover). Sparse on purpose: stages reuse
  // error/info/brand ramps for their other halves.
  stage: {
    wip: { bg: '#FFD9BE', text: '#8A3800' },
    review: { bg: '#FDDC69', text: '#684E00' },
    approved: { bg: '#A7F0BA' },
    attention: { text: '#A2191F' },
  },
  control: { hover: '#E1E1E2' },
  // Named semantic aliases over raw ramps. surface.page intentionally aliases neutral.50.
  surface: {
    page: '#FCFCF7',
    subtle: '#F6F7F0',
    muted: '#EFEFEA',
    card: '#FFFFFF',
    secondary: '#EFF1E4',
    inverse: '#1C1C1C',
  },
  text: {
    primary: '#111111',
    secondary: '#333333',
    tertiary: '#555555',
    muted: '#777777',
    disabled: '#999999',
    inverse: '#FFFFFF',
  },
  border: { subtle: '#E9E9E9', default: '#CCCCCC', strong: '#AAAAAA', interactive: '#68B631' },
  overlay: {
    10: 'rgba(0,0,0,0.1)',
    20: 'rgba(0,0,0,0.2)',
    40: 'rgba(0,0,0,0.4)',
    60: 'rgba(0,0,0,0.6)',
    80: 'rgba(0,0,0,0.8)',
  },
} as const;

export const space = {
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '20px',
  6: '24px',
  8: '32px',
  10: '40px',
  12: '48px',
  16: '64px',
} as const;

export const radius = { sm: '4px', md: '6px', lg: '8px', xl: '12px', pill: '999px' } as const;

export const layout = {
  containerMaxWidth: '1440px',
} as const;

// Effect styles (Figma node 125:1731).
export const shadow = {
  surface: '0 0 1px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.06), 0 2px 4px rgba(0,0,0,0.04)',
  field:
    '0 0 1px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.06), 0 2px 4px rgba(0,0,0,0.04), inset 0 0 0 rgba(255,255,255,0.1)',
  fieldHover: '0 0 0.5px rgba(0,0,0,0.06), 0 1px 1px rgba(0,0,0,0.06), 0 2px 2px rgba(0,0,0,0.04)',
  overlay: '0 2px 8px rgba(0,0,0,0.06), 0 -6px 12px rgba(0,0,0,0.03), 0 14px 28px rgba(0,0,0,0.08)',
  switch: '0 0 5px rgba(0,0,0,0.02), 0 2px 10px rgba(0,0,0,0.06), 0 0 1px rgba(0,0,0,0.3)',
  // tab: same value as md — named separately per Figma effect style catalog.
  tab: '0 2px 8px rgba(0,0,0,0.06)',
  inner: 'inset 0 0 1px rgba(0,0,0,0.3)',
  // Legacy aliases kept for existing component modules (sm/md/lg).
  sm: '0 0 1px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.06)',
  md: '0 2px 8px rgba(0,0,0,0.06)',
  lg: '0 2px 8px rgba(0,0,0,0.06), 0 14px 28px rgba(0,0,0,0.08)',
} as const;

export const focus = {
  // #F5F5F5 halo is the Figma-spec'd off-white glow (not a palette token).
  ring: '0 0 0 2px #F5F5F5, 0 0 0 4px rgba(142,212,90,0.4)',
  ringField: '0 0 0 2px rgba(142,212,90,0.4)',
  field: '0 0 0 1px rgba(104,182,49,0.2)',
  fieldError: '0 0 0 1px rgba(212,90,92,0.3)',
} as const;
